import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wallet, Coins, Users, Network, Zap, TrendingUp, Receipt, Store,
  CreditCard, Package, ArrowUpRight, Copy, Check, Link2, ExternalLink, Trophy, ShieldCheck, ShieldAlert, AlertTriangle,
} from "lucide-react";
import { Money, Credits } from "@/components/Currency";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const GATEWAY_LABEL = { mercadopago: "Mercado Pago", lynx: "Lynx", asaas: "Asaas" };

function Kpi({ icon: Icon, label, value, accent = "text-primary", testid }) {
  return (
    <div
      data-testid={testid}
      className="group rounded-2xl border border-border bg-card p-4 transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_16px_40px_-20px_hsl(var(--primary)/0.6)] sm:p-5"
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground sm:text-xs">{label}</span>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 ${accent} transition-transform duration-200 group-hover:scale-110`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-heading text-xl font-bold tracking-tight sm:text-3xl">{value}</div>
    </div>
  );
}

function CopyRow({ icon: Icon, label, url, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setDone(true);
      toast.success("Link copiado!");
      setTimeout(() => setDone(false), 1600);
    } catch { toast.error("Não foi possível copiar"); }
  };
  return (
    <div className="rounded-xl border border-border bg-background/40 p-4" data-testid={testid}>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-primary" /> {label}
      </div>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-muted/50 px-3 py-2 font-mono text-xs text-muted-foreground">{url}</code>
        <Button size="icon" variant="outline" onClick={copy} className="shrink-0 rounded-lg" data-testid={`${testid}-copy`}>
          {done ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export default function ResellerOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [report, setReport] = useState(null);
  const [store, setStore] = useState(null);
  const [gw, setGw] = useState(null);

  useEffect(() => {
    api.get("/reseller/dashboard").then(({ data }) => setD(data)).catch(() => {});
    api.get("/reseller/store/report").then(({ data }) => setReport(data)).catch(() => {});
    api.get("/reseller/store").then(({ data }) => setStore(data)).catch(() => {});
    api.get("/reseller/gateways").then(({ data }) => setGw(data)).catch(() => {});
  }, []);

  const credits = d?.credits ?? user?.credits ?? 0;
  const balance = d?.balance ?? user?.balance ?? 0;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const referralUrl = `${origin}/cadastro?r=${user?.id || ""}`;
  const storeUrl = store?.store_slug ? `${origin}/loja/${store.store_slug}` : null;
  const storeActive = !!store?.store_active && !!store?.store_slug;
  const activeGw = gw?.active_gateway;
  const topApps = report?.top_apps || [];

  return (
    <div className="space-y-6" data-testid="reseller-overview">
      {/* Header */}
      <div className="flex flex-col gap-4 rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Painel do Revendedor</p>
          <h2 className="mt-1 font-heading text-xl font-bold tracking-tight sm:text-3xl">Olá, {user?.name?.split(" ")[0] || "revendedor"} 🦊</h2>
          <p className="mt-1 text-sm text-muted-foreground">Sua carteira, sua rede e suas vendas em um só lugar.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
              storeActive ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500" : "border-amber-500/30 bg-amber-500/10 text-amber-500"
            }`}
            data-testid="store-status-pill"
          >
            {storeActive ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
            {storeActive ? "Loja ativa" : "Loja inativa"}
          </span>
          {storeUrl ? (
            <Button variant="outline" className="gap-2 rounded-full" onClick={() => window.open(storeUrl, "_blank")} data-testid="header-view-store">
              <ExternalLink className="h-4 w-4" /> Ver loja
            </Button>
          ) : (
            <Button variant="outline" className="gap-2 rounded-full" onClick={() => navigate("/revenda/loja")} data-testid="header-config-store">
              <Store className="h-4 w-4" /> Configurar loja
            </Button>
          )}
        </div>
      </div>

      {/* No-credits warning: sales are being routed to the platform */}
      {storeActive && activeGw && Number(credits) <= 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 sm:p-5" data-testid="reseller-no-credits-warning">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            <p className="font-heading font-semibold text-amber-600 dark:text-amber-400">Você está sem créditos</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Enquanto seus créditos estiverem zerados, as vendas via PIX da sua loja vão <b>direto para a plataforma</b> — você não recebe por elas. Recarregue para voltar a faturar. 🦊
            </p>
          </div>
          <Button size="sm" className="shrink-0 gap-2 rounded-full" onClick={() => navigate("/revenda/creditos")} data-testid="reseller-recharge-cta">
            <Wallet className="h-4 w-4" /> Recarregar
          </Button>
        </div>
      )}

      {/* KPI bento grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi icon={Wallet} label="Saldo (R$)" value={<Money value={balance} testid="reseller-balance-value" />} testid="kpi-balance" />
        <Kpi icon={Coins} label="Créditos" value={<Credits value={credits} testid="reseller-credits-badge" />} testid="kpi-credits" />
        <Kpi icon={TrendingUp} label="Faturamento" value={<Money value={report?.revenue} testid="reseller-revenue-value" />} accent="text-emerald-500" testid="kpi-revenue" />
        <Kpi icon={Receipt} label="Lucro (s/ base)" value={<Money value={report?.profit} testid="reseller-profit-value" />} accent="text-emerald-500" testid="kpi-profit" />
        <Kpi icon={Zap} label="Vendas" value={report ? report.sales : "—"} testid="kpi-sales" />
        <Kpi icon={Users} label="Diretos" value={d ? d.direct_resellers : "—"} testid="kpi-direct" />
        <Kpi icon={Network} label="Rede total" value={d ? d.network_total : "—"} testid="kpi-network" />
        <Kpi icon={Zap} label="Ativações rede" value={d ? d.network_activations : "—"} testid="kpi-activations" />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {[
          { to: "/revenda/loja", icon: Store, label: "Minha loja" },
          { to: "/revenda/recebimentos", icon: CreditCard, label: "Recebimentos" },
          { to: "/revenda/pacotes", icon: Package, label: "Pacotes" },
          { to: "/revenda/rede", icon: Network, label: "Minha rede" },
          { to: "/revenda/creditos", icon: Wallet, label: "Carteira" },
        ].map((a) => (
          <button
            key={a.to}
            onClick={() => navigate(a.to)}
            data-testid={`quick-${a.to.split("/").pop()}`}
            className="group flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-left text-sm font-medium transition-colors duration-200 hover:border-primary/50 hover:bg-primary/5 active:scale-95"
          >
            <span className="flex items-center gap-2"><a.icon className="h-4 w-4 text-primary" /> {a.label}</span>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        ))}
      </div>

      {/* Two columns: top sellers + links */}
      <div className="grid min-w-0 gap-4 lg:grid-cols-5">
        <div className="min-w-0 rounded-2xl border border-border bg-card lg:col-span-3" data-testid="top-sellers-card">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-semibold">
            <Trophy className="h-4 w-4 text-primary" /> Mais vendidos
          </div>
          {topApps.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="max-w-xs text-sm text-muted-foreground">
                Você ainda não tem vendas. Compartilhe o link da sua loja para começar a faturar! 🚀
              </p>
              {storeUrl && (
                <Button size="sm" className="gap-2 rounded-full" onClick={() => window.open(storeUrl, "_blank")} data-testid="empty-open-store">
                  <ExternalLink className="h-4 w-4" /> Abrir minha loja
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {topApps.map((a, i) => (
                <div key={a.name} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`overview-top-${i}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 font-num text-xs font-bold text-primary">{i + 1}</span>
                    <span className="truncate font-medium">{a.name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">{a.count} venda(s)</span>
                    <Money value={a.revenue} className="font-semibold" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-4 lg:col-span-2">
          <CopyRow icon={Link2} label="Seu link de indicação" url={referralUrl} testid="referral-link" />
          {storeUrl && <CopyRow icon={Store} label="Link da sua loja" url={storeUrl} testid="store-link" />}
          <div className="rounded-xl border border-border bg-background/40 p-4" data-testid="gateway-status-card">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <CreditCard className="h-4 w-4 text-primary" /> Recebimento
            </div>
            {activeGw ? (
              <p className="text-sm text-muted-foreground">
                Ativo via <span className="font-semibold text-emerald-500">{GATEWAY_LABEL[activeGw] || activeGw}</span>. O dinheiro (R$) das vendas cai direto na sua conta.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Nenhum gateway ativo — sua loja aceita só créditos. Conecte um para receber via Pix.</p>
                <Button size="sm" variant="outline" className="gap-2 rounded-full" onClick={() => navigate("/revenda/recebimentos")} data-testid="gateway-config-btn">
                  <CreditCard className="h-4 w-4" /> Configurar recebimento
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
